import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type RawAxiosHeaders,
} from 'axios'
import type { R } from '@/types/api'
import { BizError, ErrorCodes, OAuth2Error } from './error'
import { clearAccessToken, isAccessTokenUsable, setAccessToken, tokenState } from './token'

/** ADR-11 的 CSRF 第二层：跨站简单请求无法设置自定义头（附录F §13.2）。 */
export const UC_CLIENT_HEADER = 'X-UC-Client'
/** 刷新请求自身不能再触发刷新，否则死循环。 */
export const SKIP_REFRESH_HEADER = 'X-UC-Skip-Refresh'
export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api'

type RetriableConfig = AxiosRequestConfig & { _retried?: boolean }

export const http = axios.create({
  baseURL: API_BASE,
  // refresh_token 走 Cookie（uc_rt），必须带凭证
  withCredentials: true,
  timeout: 15_000,
  headers: { [UC_CLIENT_HEADER]: 'web' },
})

http.interceptors.request.use((config) => {
  const headers = new AxiosHeaders((config.headers ?? {}) as unknown as RawAxiosHeaders)
  headers.set(UC_CLIENT_HEADER, 'web')
  const token = tokenState.accessToken.value
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  config.headers = headers
  return config
})

/** 全局单飞：并发的 401 只触发一次刷新，其余请求 await 同一个 Promise（附录C §2）。 */
let refreshing: Promise<void> | null = null

function refreshOnce(attempt = 0): Promise<void> {
  if (refreshing) {
    return refreshing
  }
  refreshing = import('@/api/auth')
    .then(({ authApi }) => authApi.refresh())
    .then((pair) => setAccessToken(pair.accessToken, pair.expiresIn, pair.permVersion, {
      clientId: pair.clientId ?? null,
      systemUuid: pair.systemUuid ?? null,
    }))
    .catch(async (error: unknown) => {
      // 网络错误退避 1 次后重新判定；仍失败按 AUTH_401003 处理（清态 + 跳登录）
      if (attempt === 0 && !(error as AxiosError).response) {
        await new Promise((resolve) => setTimeout(resolve, 300))
        refreshing = null
        return refreshOnce(1)
      }
      clearAccessToken()
      await redirectToLogin()
      throw error
    })
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

/**
 * 凭据彻底失效（refresh 也换不出新令牌）时重新认证：与路由守卫一样以应用身份
 * 发起授权码 + PKCE，而不是跳 /login 走中心自有登录接口 —— 后者签出的令牌没有
 * 客户端上下文，回来还要再猜一次应用。
 *
 * 动态 import 是有意的：本模块被 `@/api/oauth` 依赖，静态引入会成环。
 */
async function redirectToLogin(): Promise<void> {
  const { default: router } = await import('@/router')
  const current = router.currentRoute.value
  // 登录页与回调页本身就在认证链路上，这里再发起授权就是把自己套进死循环
  if (current.path === '/login' || current.path === '/callback') {
    return
  }
  // 刚主动退出过：别再自动发起授权，否则会被 SSO 会话静默登录回去（与守卫同一口径）
  const { wasLoggedOut } = await import('@/utils/pkce')
  if (wasLoggedOut()) {
    void router.replace({ name: 'login', query: { redirect: current.fullPath } })
    return
  }
  const { startAuthorization } = await import('@/api/oauth')
  await startAuthorization(current.fullPath, current.path.startsWith('/me') ? 'me' : 'admin')
}

function toBizError(error: AxiosError<R<unknown>>): BizError {
  const body = error.response?.data
  if (body?.code) {
    return new BizError(body.code, body.message ?? '请求失败', body.traceId, error.response?.status)
  }
  return new BizError('SYS_500001', error.message || '网络异常，请稍后重试', undefined, error.response?.status)
}

http.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<R<unknown>>) => {
    // 协议端点（/oauth2/*、/userinfo）的错误体不是 R<T>：先在这里认领，否则会被
    // toBizError 一律压成 SYS_500001 —— 回调页就只剩一句"换取令牌失败"，
    // invalid_grant（授权码已用过/已过期）这类真因被彻底吞掉，只能靠复现猜。
    const oauthBody = error.response?.data as { error?: string; error_description?: string } | undefined
    if (typeof oauthBody?.error === 'string') {
      return Promise.reject(new OAuth2Error(oauthBody.error, oauthBody.error_description,
          error.response?.status))
    }

    const config = (error.config ?? {}) as RetriableConfig
    const status = error.response?.status
    const code = error.response?.data?.code
    const skipRefresh = (config.headers?.[SKIP_REFRESH_HEADER] as string | undefined) === '1'

    if (status !== 401 || skipRefresh) {
      // AUTH_403004：CSRF 失败，不重试，跳登录并保留 traceId 便于排查
      if (code === ErrorCodes.CSRF_FAILED) {
        clearAccessToken()
        await redirectToLogin()
      }
      return Promise.reject(toBizError(error))
    }

    // AUTH_401003：refresh 已失效 → 清态跳登录，禁止重试循环
    if (code && code !== ErrorCodes.TOKEN_EXPIRED) {
      clearAccessToken()
      await redirectToLogin()
      return Promise.reject(toBizError(error))
    }

    if (config._retried) {
      return Promise.reject(toBizError(error))
    }
    config._retried = true

    try {
      await refreshOnce()
      if (!isAccessTokenUsable()) {
        throw new Error('refresh 未返回可用令牌')
      }
      return http.request(config)
    } catch (refreshError) {
      return Promise.reject(toBizError(refreshError as AxiosError<R<unknown>>))
    }
  },
)

/** 业务码判定：成功返回 data，否则抛 BizError（HTTP 状态码只表达传输层结果）。 */
export function unwrapR<T>(body: R<T>): T {
  if (body.code === '0') {
    return body.data
  }
  throw new BizError(body.code, body.message, body.traceId)
}

/** config 用于给匿名请求打 SKIP_REFRESH（此刻还没有令牌，401 不该触发刷新）。 */
export async function get<T>(url: string, params?: Record<string, unknown>,
                             config?: AxiosRequestConfig): Promise<T> {
  const response = await http.get<R<T>>(url, { params, ...config })
  return unwrapR(response.data)
}

export async function post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await http.post<R<T>>(url, body, config)
  return unwrapR(response.data)
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.patch<R<T>>(url, body)
  return unwrapR(response.data)
}

/** 整行覆盖写（应用主题一类"没有就建、有就改"的资源）。 */
export async function put<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.put<R<T>>(url, body)
  return unwrapR(response.data)
}

export async function del<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await http.delete<R<T>>(url, { params })
  return unwrapR(response.data)
}
